import React from "react"
import ArticleMeta from "../../components/article-meta"
import CommentsForm from "../../components/comments-form"
import Layout from "../../components/layout/layout"
import SEO from "../../components/seo"
import { Articles } from "../../data/articles"

const article = Articles.getBySlug("good-managers")

export default () => (
  <Layout>
    <SEO
      title={article.title}
      keywords={[article.title, ...article.categories]}
    />
    <div id="section">
      <div className="main-article">
        <ArticleMeta article={article} />

        <div className="article-content">
          <img
            src={article.image}
            className="attachment-wide-thumbnail"
            alt={article.title}
          />
          <p>
            Gallup has found that one of the most important decisions companies
            make is simply whom they name manager. Yet our analysis suggests
            that they usually get it wrong. In fact, Gallup finds that companies
            fail to choose the candidate with the right talent for the job 82%
            of the time.
          </p>
          <p>
            Bad managers cost businesses billions of dollars each year, and
            having too many of them can bring down a company. The only defense
            against this massive problem is a good offense, because when
            companies get these decisions wrong, nothing fixes it. Businesses
            that get it right, however, and hire managers based on talent will
            thrive and gain a significant competitive advantage.
          </p>
          <p>
            Managers account for at least 70% of variance in employee engagement
            scores across business units, Gallup estimates. This variation is in
            turn responsible for severely low worldwide employee engagement.
            Gallup reported in two large-scale studies in 2012 that only 30% of
            U.S. employees are engaged at work, and a staggeringly low 13%
            worldwide are engaged. Worse, over the past 12 years these low
            numbers have barely budged, meaning that the vast majority of
            employees worldwide are failing to develop and contribute at work.
          </p>
          <p>
            Gallup has studied performance at hundreds of organizations and
            measured the engagement of 27 million employees and more than 2.5
            million work units over the past two decades. No matter the
            industry, size, or location, we find executives struggling to unlock
            the mystery of why performance varies so immensely from one
            workgroup to the next. Performance metrics fluctuate widely and
            unnecessarily within most companies, in no small part from the lack
            of consistency in how people are managed. This “noise” frustrates
            leaders because unpredictability causes great inefficiencies in
            execution.
          </p>
          <p>
            Executives can cut through this noise by measuring what matters
            most. Gallup has discovered links between employee engagement at the
            business-unit level and vital performance indicators, including
            customer metrics; higher profitability, productivity, and quality
            (fewer defects); lower turnover; less absenteeism and shrinkage
            (i.e., theft); and fewer safety incidents. When a company raises
            employee engagement levels consistently across every business unit,
            everything gets better.
          </p>
          <p>
            To make this happen, companies should systematically demand that
            every team within their workforce have a great manager. After all,
            the root of performance variability lies within human nature itself.
            Teams are composed of individuals with diverging needs related to
            morale, motivation, and clarity — all of which lead to varying
            degrees of performance. Nothing less than great managers can
            maximize them.
          </p>
          <p>But first, companies have to find those great managers.</p>
          <p>
            If great managers seem scarce, it’s because the talent required to
            be one is rare. Gallup finds that great managers have the following
            talents:
          </p>
          <p>
            <ul>
              <li>
                They motivate every single employee to take action and engage
                them with a compelling mission and vision.
              </li>
              <li>
                They have the assertiveness to drive outcomes and the ability to
                overcome adversity and resistance.
              </li>
              <li>They create a culture of clear accountability.</li>
              <li>
                They build relationships that create trust, open dialogue, and
                full transparency.
              </li>
              <li>
                They make decisions that are based on productivity, not
                politics.
              </li>
            </ul>
          </p>
          <p>
            Gallup’s research reveals that about one in ten people possess all
            these necessary traits. While many people are endowed with some of
            them, few have the unique combination of talent needed to help a
            team achieve excellence in a way that significantly improves a
            company’s performance. These 10%, when put in manager roles,
            naturally engage team members and customers, retain top performers,
            and sustain a culture of high productivity. Combined, they
            contribute about 48% higher profit to their companies than average
            managers.
          </p>
          <p>
            It’s important to note that another two in 10 exhibit some
            characteristics of basic managerial talent and can function at a
            high level if their company invests in coaching and developmental
            plans for them.
          </p>
          <CommentsForm title={article.title} />
        </div>
      </div>
    </div>
  </Layout>
)
